import {
  InvestmentState,
  type InvestmentCreateRequest,
  type InvestmentTermsRequest,
  type InvestmentResponse,
  type InvestmentUpdateRequest,
} from "@/domain/investment";
import type { BeneficiaryDetail, FiscalQuota } from "@/domain/beneficiary";
import type { InvestorSummary } from "@/domain/investor";
import type { InvestmentFormData } from "@/domain/validation/investmentSchemas";

/** Create a blank form state. */
export function createInitialFormData(
  preselectedProject?: BeneficiaryDetail | null,
  preselectedInvestor?: InvestorSummary | null,
  preselectedState: InvestmentState = InvestmentState.PROSPECT,
): InvestmentFormData {
  return {
    projectId: preselectedProject?.uuid || "",
    investorId: preselectedInvestor?.uuid || "",
    quotaId: "",
    amount: "",
    returnPercentage: "",
    status: preselectedState,
  };
}

export function buildInvestmentUpdateRequest(
  values: InvestmentFormData,
  quotaUuid: string,
): InvestmentUpdateRequest {
  return {
    amount: values.amount,
    return_percentage: values.returnPercentage,
    fiscal_quota_uuid: quotaUuid,
    state: values.status,
  };
}
export function buildInvestmentTermsRequest(
  values: InvestmentFormData,
  quotaUuid: string,
): InvestmentTermsRequest {
  return {
    fiscal_quota_uuid: quotaUuid,
    amount: values.amount,
    return_percentage: values.returnPercentage,
    state: values.status,
  };
}

export function buildInvestmentCreateRequest(
  values: InvestmentFormData,
  investorUuid: string,
  quotaUuid: string,
): InvestmentCreateRequest {
  return { ...buildInvestmentTermsRequest(values, quotaUuid), investor_uuid: investorUuid };
}
export function selectEligibleInvestmentQuotas(
  quotas: FiscalQuota[] | undefined,
  editingQuotaUuid?: string,
): FiscalQuota[] {
  return quotas
    ? quotas.filter(
        (q) =>
          (q.money && Number.parseFloat(q.money.deduction.available) > 0) ||
          q.uuid === editingQuotaUuid,
      )
    : [];
}
export function selectCurrentYearInvestmentQuota(
  quotas: FiscalQuota[] | undefined,
  currentYear: number,
): FiscalQuota | undefined {
  return quotas?.find(
    (q) => q.fiscal_year === currentYear && Number.parseFloat(q.money.deduction.available) > 0,
  );
}

/** Preserve the current correction form, including its initially blank note. */
export function investmentToEditForm(investment: InvestmentResponse): InvestmentFormData {
  return {
    projectId: investment.beneficiary.uuid,
    investorId: investment.investor.uuid,
    quotaId: investment.quota.uuid,
    amount: investment.amount,
    returnPercentage: investment.return_percentage,
    status: investment.state,
  };
}

/** Apply the default only once per quota selection; preserve manual edits. */
export function getInvestmentQuotaAutofill(
  quota: Pick<FiscalQuota, "uuid" | "default_profitability_percentage"> | null,
  previousUuid: string | null,
) {
  const uuid = quota?.uuid;
  if (!uuid) return { lastQuotaUuid: null, updates: null };
  return {
    lastQuotaUuid: uuid,
    updates:
      uuid !== previousUuid && quota.default_profitability_percentage
        ? { returnPercentage: quota.default_profitability_percentage }
        : null,
  };
}
